// Thoughts/blog data with complete content

export const THOUGHTS = [
  {
    id: 1,
    title: 'On Building with AI',
    excerpt: 'The intersection of creativity and machine learning opens new possibilities...',
    content: `The intersection of creativity and machine learning opens new possibilities that we're only beginning to explore.

When I started working with large language models, I expected them to be sophisticated text generators. What I found instead was something more profound—a thinking partner that could help crystallize ideas still forming in my mind.

The key insight is that AI doesn't replace human creativity; it amplifies it. The best results come from a dance between human intuition and machine capability. You bring the vision, the taste, the judgment. The AI brings speed, breadth, and tireless iteration.

But there's a trap here. It's easy to let the AI do too much, to accept its first suggestions without pushing back. The magic happens in the refinement, in asking "what if we tried this instead?" The AI is a mirror that reflects your ideas back with new angles you hadn't considered.

Building with AI requires a new skill: prompt craft. It's part writing, part programming, part psychology. You learn to structure your thoughts in ways that unlock the model's best capabilities. You learn when to be specific and when to leave room for surprise.

The future of creative work isn't human vs. machine. It's human with machine—a collaboration where each partner contributes what they do best.`,
    date: '2025-01-15',
    category: 'Technology',
  },
  {
    id: 2,
    title: 'Designing for the Future',
    excerpt: 'As interfaces evolve, so must our understanding of human-computer interaction...',
    content: `As interfaces evolve, so must our understanding of human-computer interaction. The screens we stare at today will seem as antiquated as punch cards to future generations.

The trajectory is clear: interfaces are becoming more natural, more ambient, more invisible. Voice, gesture, thought—these are the interaction modalities waiting in the wings. The best interface is no interface at all, or at least one so intuitive it disappears from conscious awareness.

But here's the paradox: as interfaces become simpler on the surface, they become more complex underneath. The AI systems powering natural interactions require vast computational resources. Simplicity for the user demands sophistication in the system.

I've been thinking about what I call "anticipatory design"—systems that understand not just what you're asking for, but what you'll need next. The goal is to eliminate the cognitive load of navigation, of finding, of remembering. The system should know.

Of course, this raises questions about privacy, autonomy, and control. How much should a system anticipate? When does helpful become intrusive? These are design decisions with ethical weight.

The future I'm designing for is one where technology feels less like a tool and more like an extension of self. Not in a cyborg sense, but in the way a musician's instrument becomes part of their expression. Seamless. Natural. Human.`,
    date: '2025-01-10',
    category: 'Design',
  },
  {
    id: 3,
    title: 'The Art of Simplicity',
    excerpt: 'In a world of complexity, simplicity becomes a radical act of design...',
    content: `In a world of complexity, simplicity becomes a radical act of design. Every feature you don't add is a gift to your users.

I've learned this lesson the hard way, through projects bloated with well-intentioned additions that collectively created confusion. The best work I've done has come from saying no—no to the extra button, no to the additional setting, no to the clever feature that solves a problem nobody actually has.

Simplicity isn't about doing less. It's about focusing on what matters. It's the confidence to strip away until only the essential remains. This requires taste, judgment, and the courage to disappoint people who want more.

The Japanese concept of "ma"—negative space—has influenced my thinking. The empty space isn't nothing; it's the context that gives meaning to what's there. In interface design, white space isn't wasted space. It's breathing room for attention.

There's also a temporal dimension to simplicity. Simple things are faster to understand, faster to use, faster to master. They respect the user's time, which is ultimately a form of respect for the user themselves.

The hardest part of simplicity is that it looks easy. People assume the simple solution was obvious, that it didn't require effort. The truth is the opposite: the simple solution is often the last one you find, after exploring and discarding the complex alternatives.

Simplicity is not the starting point. It's the destination.`,
    date: '2025-01-05',
    category: 'Philosophy',
  },
  {
    id: 4,
    title: 'Learning in Public',
    excerpt: 'Sharing your journey, mistakes and all, accelerates growth in unexpected ways...',
    content: `Sharing your journey, mistakes and all, accelerates growth in unexpected ways. The fear of looking foolish is the greatest barrier to learning.

I used to hoard my work until it was "ready"—polished, complete, defensible. This protected my ego but stunted my growth. The feedback I needed most came too late to be useful.

Learning in public changes the equation. When you share early and often, you get course corrections while you can still adjust. You find collaborators who share your interests. You build a body of work that demonstrates not just what you know, but how you think.

The key insight is that showing your work isn't bragging. It's generous. Others are on similar journeys and can learn from your struggles. Your solution to a problem might save someone else hours of frustration.

There's also an accountability aspect. When you commit publicly to learning something, you're more likely to follow through. The audience—even an imagined one—provides motivation that pure self-improvement lacks.

The hardest part is accepting that you'll be wrong publicly. You'll share things you'll later cringe at. But that cringe is evidence of growth. If you're not embarrassed by your past work, you haven't learned enough.

Learning in public isn't about having answers. It's about asking better questions, out loud, where others can join the inquiry.`,
    date: '2024-12-28',
    category: 'Growth',
  },
  {
    id: 5,
    title: 'The Texture of Time',
    excerpt: 'Not all hours are created equal. Some are heavy with possibility, others slip away...',
    content: `Not all hours are created equal. Some are heavy with possibility, others slip away like water through fingers.

I've become fascinated by the subjective experience of time—how it stretches during moments of deep focus and compresses during distraction. The same sixty minutes can contain multitudes or nothing at all.

The flow state, that mythologized condition of peak performance, is really a temporal phenomenon. Time disappears because attention is so complete that there's no room left for self-consciousness, for clock-watching, for the meta-awareness that usually fractures our experience.

I've started treating time as texture rather than quantity. Some hours are rough and granular, full of interruption and context-switching. Others are smooth and continuous, allowing deep immersion. The goal isn't more time but better texture.

Rituals help. The morning coffee, the afternoon walk, the evening review—these create temporal landmarks that give shape to the day. Without them, time becomes an undifferentiated blur.

The texture of time is also social. Hours spent with people who energize you feel different from hours with those who drain. Some conversations make time fly; others make minutes feel eternal.

I'm learning to be a better curator of my temporal experience. To protect the smooth hours. To accept the rough ones as necessary. To remember that how time feels matters as much as how it's spent.`,
    date: '2024-12-20',
    category: 'Philosophy',
  },
  {
    id: 6,
    title: 'Code as Craft',
    excerpt: 'There is an aesthetic dimension to programming that goes beyond functionality...',
    content: `There is an aesthetic dimension to programming that goes beyond functionality. Code can be beautiful, and that beauty matters.

I'm not talking about clever tricks or obscure one-liners. Those are showing off, not craft. True code beauty comes from clarity, from the perfect match between problem and solution, from names that reveal intent.

The best code reads like prose. It tells a story of transformation, of input becoming output through a series of logical steps that feel inevitable in retrospect. Reading it, you think: of course, how else could it work?

Craft means caring about things that don't "matter." The consistent indentation, the thoughtful comments, the well-chosen variable names—these don't change functionality, but they change the experience of working with the code. They're a gift to your future self and your colleagues.

There's also craft in restraint. Not using a design pattern because you can, but because it genuinely fits. Not abstracting prematurely. Not optimizing before you've measured. Knowing when good enough is good enough.

The craft of code extends to the surrounding artifacts: the commit messages that explain why, the tests that document behavior, the documentation that bridges the gap between what the code does and what users need.

Code as craft isn't about perfection. It's about intentionality. Every choice should be a choice, not a default.`,
    date: '2024-12-15',
    category: 'Technology',
  },
]

export const THOUGHTS_META = {
  logoText: 'Nexus.',
  heroHeading: ['Where matter becomes', 'thought and thought', 'becomes form'],
  heroSubheading: [
    'our vessel drifts at coordinates (0.00, 0.00)',
    'gravitational field extends 0.10 units into quantum foam',
    'currently merging with 0 other entities',
  ],
  contactHeading: '+Get in touch',
  contactEmail: 'hi@example.com',
  footerLinks: [
    { label: 'Fluid Dynamics', url: '#' },
    { label: 'Organic Shapes', url: '#' },
    { label: 'Interactive Forms', url: '#' },
    { label: 'Motion Studies', url: '#' },
    { label: 'Contact', url: '#' },
  ],
  statusLines: ['Nexus State • Active', 'where consciousness flows'],
}
